<template>
  <SlidingPanel :isPanelOpen="campaignSelectorStore.isSlidingPanelOpen" :isSubPanelOpen="campaignSelectorStore.isSubPanelOpen" @closePanel="campaignSelectorStore.closePanel()" @closeSubPanel="campaignSelectorStore.closeSubPanel()">
    <template #content>
      <CampaignManagementView v-if="campaignSelectorStore.currentSlidingPanel === 'campaign-management'" :campaign="campaignSelectorStore.selectedCampaign" @close="campaignSelectorStore.closePanel()" @openSubPanel="campaignSelectorStore.openSubPanel($event)"></CampaignManagementView>
      <CampaignInfosPanel v-else-if="campaignSelectorStore.currentSlidingPanel === 'edit-informations'" :campaign="campaignSelectorStore.selectedCampaign" @close="campaignSelectorStore.closeSubPanel()"></CampaignInfosPanel>
      <EvaluationConfigPanel v-else-if="campaignSelectorStore.currentSlidingPanel === 'edit-weightings'" :campaign="campaignSelectorStore.selectedCampaign" @close="campaignSelectorStore.closeSubPanel()" :disableIgnored="true" :footerActivated="false"></EvaluationConfigPanel>
      <CampaignCardPanel v-else-if="campaignSelectorStore.currentSlidingPanel === 'edit-card'" :campaign="campaignSelectorStore.selectedCampaign" @close="campaignSelectorStore.closeSubPanel()"></CampaignCardPanel>
      <AddMemberPanel v-else-if="campaignSelectorStore.currentSlidingPanel === 'add-consultant'" :scope="{ kind: ScopeRefKind.CAMPAIGN, id: campaignSelectorStore.selectedCampaign.id }" @close="campaignSelectorStore.closeSubPanel()"></AddMemberPanel>
      <CreateCampaignPanel v-else-if="campaignSelectorStore.currentSlidingPanel === 'create-campaign'" @close="campaignSelectorStore.closePanel()"></CreateCampaignPanel>
    </template>
  </SlidingPanel>
  <transition name="fade" mode="out-in">
    <div class="campaigns-management-wrapper" :key="space?.workspace_id">
      <Header :name="$t('app.sidebar.items.campaigns')" back="space-selector" />
      <div v-if="!isPendingSpace && !isRolesPending" class="campaigns-management-body">
        <div class="button-header">
          <Button v-if="canWriteCampaigns" data-step="create-campaign-button" class="icon icon-left primary light-text m" :label="$t('app.spaces.spaceView.createCampaign')" @click="campaignSelectorStore.openCreateCampaignPanel()">
            <template #prefix>
              <span class="material-symbols-outlined">add</span>
            </template>
          </Button>
          <VField :class="canWriteCampaigns ? 'put-to-end' : undefined" class="light search m white-background" :placeholder="$t('app.campaign.single.search')" name="search" icon="bi-search" v-model="state.search.value" @input="(event) => (state.search.value = event.target.value)" />
        </div>
        <div class="campaigns-table" data-step="campaign-list">
          <CampaignsTable :search="state.search" @edit-campaign="campaignSelectorStore.openPanel"></CampaignsTable>
        </div>
      </div>
      <ActionModal v-if="campaignSelectorStore.selectedCampaign" modal="archive" :data="campaignSelectorStore.selectedCampaign" :isOpen="state.showActionModal" @close="state.showActionModal = false" @confirm="state.showActionModal = false" />
    </div>
  </transition>
</template>

<style lang="scss" scoped>
  .button-header {
    display: flex;
    flex-direction: row;
    align-items: end;
    gap: 8px;

    .put-to-end {
      margin-left: auto;
    }
  }

  .row-text {
    max-width: 300px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap
  }

  .campaigns-table {
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  .search {
    width: 350px;
    height: fit-content;
  }
</style>

<script setup lang="ts">
import { reactive } from "vue";
import { useRoute } from "vue-router";
import { useDark } from "@vueuse/core";

import { dateIsBefore } from "@/composables/utils.js";

import Header from "@/components/Header.vue";
import ActionModal from "@/components/modals/app/actionModal/ActionModal.vue";
import SlidingPanel from "@/components/sliding-panel/SlidingPanel.vue";
import CampaignInfosPanel from "@/components/sliding-panel/campaign/CampaignInfosPanel.vue";
import EvaluationConfigPanel from "@/components/sliding-panel/campaign/EvaluationConfigPanel.vue";
import CampaignCardPanel from "@/components/sliding-panel/campaign/CampaignCardPanel.vue";
import AddMemberPanel from "@/components/sliding-panel/AddMemberPanel.vue";

import { useCampaigns } from "@/queries/campaigns.query";
import { useRoles } from "@/queries/user.query";
import { useSpace } from "@/queries/space.query";

import { Campaign } from "@/types/campaign";

import PlusIcon from '@/assets/images/icon/campaign/plus.svg?component';
import codeRomeData from '@/assets/correspondance_rome/correspondance_code_rome.json';
import intituleRomeData from '@/assets/correspondance_rome/correspondance_intitule_rome.json';

import CampaignManagementView from "./CampaignManagementView.vue";
import { MemberRole } from "@/types/role.js";
import CreateCampaignPanel from "@/components/sliding-panel/campaignCreation/CreateCampaignPanel.vue";
import CampaignsTable from "@/components/global/tables/CampaignsTable.vue";
import { ScopeRefKind } from "@/types/member.js";

type PanelType = 'campaign-management' | 'edit-informations' | 'edit-weightings' | 'edit-card' | 'add-consultant';

interface JobCategorie {
  categorie_number: number,
  generic_categorie: string,
}

type RomeMap = Record<string, JobCategorie>;

const codeRome = Object.entries(codeRomeData as RomeMap);
const intituleRome = Object.entries(intituleRomeData as RomeMap);

const isDark = useDark();
const route = useRoute();
const router = useRouter();
const appStore = useAppStore();
const campaignSelectorStore = useCampaignSelectorStore();
const { t } = useI18n();

const { canWriteCampaigns } = usePermissions();
const { data: roles, isPending: isRolesPending } = useRoles();
const { data: space, isPending: isPendingSpace } = useSpace();
const { data: campaigns, isPending } = useCampaigns();

const state = reactive({
  currentPanel: null as PanelType | null,
  modalIsOpen: false,
  showParameters: false,
  showActionModal: false,
  campaign: null,
  search: {
    value: "",
    searchable: [
      "campaign.internal_name",
    ],
  },
});

const filteredCampaigns = computed(() => {
  if (isPending.value) return [];
  const searchString = state.search.value.trim().toLocaleLowerCase();
  if (searchString === "") return [...campaigns.value].sort((a, b) => a.campaign.internal_name.localeCompare(b.campaign.internal_name));

  const filteredRomeIntitule = intituleRome.filter((intitule) => {
    return intitule[0].includes(searchString);
  });

  const filteredRomeCode = codeRome.filter((code) => {
    return code[0].toLocaleLowerCase() === searchString;
  });

  const res = campaigns.value.filter((campaign) => {
    const campaignName = campaign.campaign.internal_name.trim().toLocaleLowerCase();
    if (campaignName.includes(searchString)) return true;
    if (filteredRomeIntitule.find((intitule) => campaignName.includes(intitule[1].generic_categorie))) return true;
    if (filteredRomeCode.find((code) => campaignName.includes(code[1].generic_categorie))) return true;
    return false;
  }).sort((a, b) => a.campaign.internal_name.localeCompare(b.campaign.internal_name));
  console.log(res);
  return res;
});

function goToCreateCampaign() {
  router.push({
    name: 'newCampaign',
    query: {
      spaceId: route.params.spaceId,
    },
  })
}

function goToCampaign(campaign: Campaign) {
  router.push({
    name: "campaign",
    params: {
      spaceId: appStore.currentSpaceId,
      campaignId: campaign.id,
    }
  });
}

async function archiveCampaign(campaign: Campaign) {
  campaignSelectorStore.selectedCampaign = campaign;
  state.showActionModal = true;
}

watch(campaigns, () => {
  if (campaignSelectorStore?.selectedCampaign) {
    campaignSelectorStore.selectedCampaign = campaigns.value.find((campaign) => campaign.id === campaignSelectorStore?.selectedCampaign?.id);
  }
});
</script>

<style lang="scss" scoped>
@import '@/assets/style/variables.scss';

.separator {
  width: 100%;
  height: 1px;
  background-color: var(--light-grey-color);
}

.campaigns-management-wrapper {
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  overflow: hidden;
  margin: 0 auto;
  width: 100%;
  max-width: 900px;

  .campaigns-management-body {
    height: 100%;
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;

    .button-header {
      display: flex;
      margin-bottom: 16px;

      .put-to-end {
        margin-left: auto;
      }
    }
  }

  .center-field {
    justify-content: center !important;
  }

  .logo {
    width: 32px;
  }
}
</style>